import { Button, Form, Input, Row, Select, Space } from 'antd';
import { useSelectedEvent } from 'providers/Calendar/hooks';
import {
  forwardRef,
  ForwardRefRenderFunction,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';
import { useTranslation } from 'react-i18next';
import { CloseOutlined, DeleteFilled, CheckOutlined } from '@ant-design/icons';
import { useCalendarDispatchContext } from 'providers/Calendar';
import {
  CALENDAR_EVENT_REPEATING,
  CALENDAR_EVENT_STATUSES
} from 'providers/Calendar/constants';
import TextArea from 'antd/lib/input/TextArea';
import dayjs from 'dayjs';
import { CalendarEventType } from 'providers/Calendar/types';

import { ColorItem, StyledEventDropdown, StyledEventHeader } from './styled';
import { DROPDOWN_HEIGHT, DROPDOWN_WIDTH, LEFT_EXTRA } from './constants';
import { EventFormValues, IEventDropdownRef } from './types';
import { EVENT_COLORS } from 'pages/Calendar/constants';

const EventDropdown: ForwardRefRenderFunction<IEventDropdownRef> = (_, ref) => {
  // context
  const { setActiveEvent, updateEvent, deleteEvent } =
    useCalendarDispatchContext();

  const item = useSelectedEvent();
  // form
  const [form] = Form.useForm();
  // translations
  const { t } = useTranslation();
  // states
  const [selectedColor, setColor] = useState('');

  useEffect(() => {
    form.setFieldsValue({
      title: item?.event?.title || '',
      repeat:
        item?.event?.extendedProps?.repeat ||
        CALENDAR_EVENT_REPEATING.no_repeat,
      description: item?.event?.extendedProps?.description || ''
    });
    setColor(item?.event.color || '');
  }, [form, item]);

  const onClose = useCallback(() => {
    setActiveEvent(null);
  }, [setActiveEvent]);

  const onCancel = useCallback(() => {
    if (item?.event?.extendedProps?.status === CALENDAR_EVENT_STATUSES.new) {
      deleteEvent(item.event.id);
    }

    onClose();
  }, [deleteEvent, item, onClose]);

  const onFinish = useCallback(
    (values: EventFormValues) => {
      if (!item?.event) return;
      const current = { ...item.event };
      let data: CalendarEventType = {
        id: item.event.id,
        color: selectedColor,
        title: values.title,
        extendedProps: {
          status: CALENDAR_EVENT_STATUSES.existing,
          description: values.description,
          repeat: values.repeat
        }
      };

      const startTime =
        current.startTime || dayjs(current.start).format('HH:mm');

      const endTime = current.endTime || dayjs(current.end).format('HH:mm');
      const startRecur = current.startRecur || dayjs(current.start).format();

      switch (values.repeat) {
        case CALENDAR_EVENT_REPEATING.daily: {
          delete current.start;
          delete current.end;

          data = {
            ...current,
            ...data,
            editable: false,
            startTime,
            endTime,
            startRecur
          };
          break;
        }

        case CALENDAR_EVENT_REPEATING.daily_weekdays: {
          // get working hours
          const daysOfWeek = current.daysOfWeek || ['1', '2', '3', '4', '5'];

          delete current.start;
          delete current.end;

          data = {
            ...current,
            ...data,
            editable: false,
            startTime,
            endTime,
            startRecur,
            daysOfWeek
          };
          break;
        }

        case CALENDAR_EVENT_REPEATING.weekly: {
          const daysOfWeek = current.daysOfWeek || [
            String(dayjs(current.start).day())
          ];

          delete current.start;
          delete current.end;

          data = {
            ...current,
            ...data,
            editable: false,
            startTime,
            endTime,
            startRecur,
            daysOfWeek
          };
          break;
        }

        default:
          data = { ...current, ...data };
          break;
      }

      updateEvent(data);
      onClose();
    },
    [item, onClose, selectedColor, updateEvent]
  );

  const vertical = useMemo(() => {
    if (!item) return {};

    if (window.innerHeight - DROPDOWN_HEIGHT <= item.ui.y + 50) {
      return { bottom: 20 };
    }

    return { top: item.ui.y };
  }, [item]);

  const horizontal = useMemo(() => {
    if (!item) return {};

    if (DROPDOWN_WIDTH >= item.ui.x) {
      return { left: 20 };
    }

    if (window.innerWidth - DROPDOWN_WIDTH <= item.ui.x) {
      return { right: 20 };
    }

    return { left: item.ui.x - LEFT_EXTRA };
  }, [item]);

  useImperativeHandle(ref, () => ({
    close: onCancel
  }));

  return (
    <StyledEventDropdown open={!!item} {...vertical} {...horizontal}>
      <StyledEventHeader>
        <Row justify="end">
          <Button
            type="text"
            onClick={() => item && deleteEvent(item.event.id)}
            danger
          >
            <DeleteFilled />
          </Button>
          <Button type="text" onClick={onCancel}>
            <CloseOutlined />
          </Button>
        </Row>
      </StyledEventHeader>
      <Form form={form} layout="vertical" size="middle" onFinish={onFinish}>
        <Form.Item
          name="title"
          label={t('calendar.event_title')}
          rules={[{ required: true }]}
          style={{ marginBottom: 8 }}
        >
          <Input />
        </Form.Item>
        <Form.Item
          name="repeat"
          label={t('calendar.repeat')}
          rules={[{ required: false }]}
          style={{ marginBottom: 8 }}
        >
          <Select
            disabled={
              item?.event.extendedProps?.status ===
              CALENDAR_EVENT_STATUSES.existing
            }
          >
            <Select.Option value={CALENDAR_EVENT_REPEATING.no_repeat}>
              {t('calendar.no_repeat')}
            </Select.Option>
            <Select.Option value={CALENDAR_EVENT_REPEATING.daily}>
              {t('calendar.daily')}
            </Select.Option>
            <Select.Option value={CALENDAR_EVENT_REPEATING.daily_weekdays}>
              {t('calendar.daily_weekdays')}
            </Select.Option>
            <Select.Option value={CALENDAR_EVENT_REPEATING.weekly}>
              {t('calendar.weekly')}
            </Select.Option>
          </Select>
        </Form.Item>
        <Form.Item
          name="description"
          label={t('calendar.description')}
          rules={[{ required: false }]}
          style={{ marginBottom: 8 }}
        >
          <TextArea style={{ resize: 'none' }} />
        </Form.Item>
        <Space direction="vertical" style={{ width: '100%' }} size={24}>
          <Space size={8} wrap>
            {EVENT_COLORS.map(color => {
              const isActive = selectedColor === color;

              return (
                <ColorItem
                  type="button"
                  key={color}
                  color={color}
                  onClick={() => item && setColor(color)}
                >
                  <CheckOutlined
                    style={{
                      color: isActive ? 'var(--white)' : 'transparent'
                    }}
                  />
                </ColorItem>
              );
            })}
          </Space>
          <Row justify="end">
            <Button htmlType="submit" type="primary">
              {t('common.save')}
            </Button>
          </Row>
        </Space>
      </Form>
    </StyledEventDropdown>
  );
};

export default memo(forwardRef(EventDropdown));
